import React from 'react'
import { useTranslation } from 'react-i18next'

import { SectionTitle } from '../../../atoms/SectionTitle'
import {
  TransportStopsTable,
  StopRow,
  StopIcon,
  StopName,
  StopDistance,
  type StopType,
} from '../../../molecules/TransportStopsTable'

import {
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  Box,
  Icon,
  QualityBadge,
  QualityClass,
} from '../../../atoms'
import {
  orderedModes,
  bestHeadwayMin,
  formatHeadwayMinutes,
  transitLines,
} from '../../Amenities/transitDisplay'
import {
  AmenityLineBadges,
  AmenityLineBadge,
} from '../../Amenities/Amenities.styles'
import { useTransportAndAccess } from '../TransportAndAccessContext'
import { categorizeTransitStops } from '../transitStops'
import { StopMeta } from '../TransportAndAccessSection.styles'

const STOP_ICON_BY_POI_TYPE: Record<string, { type: StopType; icon: string }> =
  {
    atlas_train_station: { type: 'train', icon: 'train' },
    atlas_metro_station: { type: 'metro', icon: 'train-front' },
    atlas_tram_stop: { type: 'tram', icon: 'tram-front' },
    atlas_bus_stop: { type: 'bus', icon: 'bus' },
    atlas_boat_stop: { type: 'other', icon: 'ship' },
    atlas_cable_car_stop: { type: 'other', icon: 'mountain' },
  }

// Same cap as the Amenities card, whose badges these rows now mirror.
const MAX_VISIBLE_LINES = 5

export const TransportAndAccessPublicTransport: React.FC = () => {
  const { t } = useTranslation('transport')
  const { publicTransportStops, publicTransportMetrics } =
    useTransportAndAccess()

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} ${t('units.kilometers', { ns: 'transport' })}`
    }
    return `${Math.round(meters)} ${t('units.meters', { ns: 'transport' })}`
  }

  // Categorize stops by their poi_type, the authoritative field since the ES migration.
  const categorizedStops = categorizeTransitStops(publicTransportStops)

  /*
    A stop typed atlas_tram_stop can also serve buses; `transit.modes` is the full
    truth and the icon only reflects the highest mode. A multi-mode stop gets the
    neutral hub glyph, as before.
  */
  const getStopType = (stop: any) => {
    const modes = orderedModes(stop?.transit)
    const mapped = STOP_ICON_BY_POI_TYPE[stop?.poi_type] ?? {
      type: 'bus' as StopType,
      icon: 'bus',
    }
    const label = modes
      .map((mode) =>
        t(`transitModes.${mode}`, { ns: 'amenities', defaultValue: mode })
      )
      .join(' · ')

    if (modes.length > 1)
      return { type: 'hub' as StopType, icon: 'route', label }
    return { ...mapped, label: label || t('publicTransport.unknownStop') }
  }

  const stopInterval = (stop: any): string | null => {
    const headway = bestHeadwayMin(stop?.transit)
    if (headway === null) return null
    const minutes = formatHeadwayMinutes(headway)
    return t('publicTransport.everyMinutes', {
      count: Number(minutes),
      minutes,
      defaultValue: minutes === '1' ? 'every minute' : `every ${minutes} min`,
    })
  }

  /*
    `transit.lines` is not indexed in ES yet, so this returns [] for every row
    today and the meta line looks exactly as it did before this badge was
    added. The code goes in ahead of the data rather than waiting for it.
  */
  const stopMetaSegments = (
    stop: any,
    label: string | undefined
  ): React.ReactNode[] => {
    const segments: React.ReactNode[] = [label, stopInterval(stop)].filter(
      Boolean
    )

    const lines = transitLines(stop?.transit)
    if (lines.length) {
      const shown = lines.slice(0, MAX_VISIBLE_LINES)
      const hidden = lines.slice(MAX_VISIBLE_LINES)
      segments.push(
        <AmenityLineBadges key="lines">
          {shown.map((line) => (
            <AmenityLineBadge key={line.key}>{line.label}</AmenityLineBadge>
          ))}
          {hidden.length > 0 && (
            <AmenityLineBadge
              $muted
              title={hidden.map((line) => line.label).join(', ')}
            >
              +{hidden.length}
            </AmenityLineBadge>
          )}
        </AmenityLineBadges>
      )
    }

    return segments
  }

  // Summary metrics
  const summaryMetrics = [
    {
      title: t('publicTransport.metrics.qualityClass.title'),
      value: publicTransportMetrics?.qualityClass ?? null,
      icon: 'target',
      color: '#8b5cf6',
      isClass: true,
    },
    {
      title: t('publicTransport.metrics.totalStops1km.title'),
      value: publicTransportStops.length,
      icon: 'map-pin-filled',
      color: '#3b82f6',
    },
    {
      title: t('publicTransport.metrics.busStops.title'),
      value: categorizedStops.bus.length,
      icon: 'bus',
      color: '#f59e0b',
    },
    {
      // Metro stops are already folded into the tram bucket (Lausanne m1/m2 are
      // urban rail, categorized alongside trams), so no separate union is needed.
      title: t('publicTransport.metrics.tramMetro.title'),
      value: categorizedStops.tram.length,
      icon: 'tram-front',
      color: '#3b82f6',
    },
    {
      title: t('publicTransport.metrics.trainStations.title'),
      value: categorizedStops.train.length,
      icon: 'train',
      color: '#ef4444',
    },
    {
      title: t('publicTransport.metrics.stopsUnder500m.title'),
      value: publicTransportMetrics?.totalStopsWithin500m || 0,
      icon: 'target',
      color: '#10b981',
    },
  ]

  return (
    <>
      <SectionTitle>{t('publicTransport.overview')}</SectionTitle>
      <AnalysisMetricsGrid>
        {summaryMetrics.map((metric) => (
          <AnalysisMetricCard key={metric.title}>
            <MetricHeader>
              <MetricIcon color={metric.color}>
                <Icon name={metric.icon as any} size="small" />
              </MetricIcon>
              <MetricTitle>{metric.title}</MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              {metric.isClass ? (
                metric.value ? (
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <QualityBadge qualityClass={metric.value as QualityClass}>
                      {t('overview.class')} {metric.value}
                    </QualityBadge>
                  </Box>
                ) : (
                  <MetricValue>
                    {t('publicTransport.metrics.qualityClass.none')}
                  </MetricValue>
                )
              ) : (
                <MetricValue>{metric.value}</MetricValue>
              )}
            </Box>
          </AnalysisMetricCard>
        ))}
      </AnalysisMetricsGrid>

      <Box mt="32px">
        <SectionTitle>{t('publicTransport.nearbyStops')}</SectionTitle>
        <TransportStopsTable>
          {publicTransportStops.slice(0, 10).map((stop) => {
            const stopType = getStopType(stop)
            return (
              <StopRow key={stop.id}>
                <StopIcon type={stopType.type}>
                  <Icon name={stopType.icon as any} size="small" />
                </StopIcon>
                <StopName>
                  {stop.name || t('publicTransport.unknownStop')}
                  <StopMeta>
                    {stopMetaSegments(stop, stopType.label).map(
                      (segment, index) => (
                        <React.Fragment key={index}>
                          {index > 0 && ' · '}
                          {segment}
                        </React.Fragment>
                      )
                    )}
                  </StopMeta>
                </StopName>
                <StopDistance>
                  {stop.distance
                    ? formatDistance(stop.distance)
                    : t('units.na', { ns: 'transport' })}
                </StopDistance>
              </StopRow>
            )
          })}
        </TransportStopsTable>
        {publicTransportStops.length === 0 && (
          <Box
            p="24px"
            style={{
              textAlign: 'center',
              color: '#6b7280',
              background: '#f9fafb',
              borderRadius: '8px',
            }}
          >
            {t('publicTransport.noStopsFound')}
          </Box>
        )}
      </Box>
    </>
  )
}
